import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Card from '../../common/Card/Card';
import Button from '../../common/Button/Button';
import Notification from '../../common/Notification/Notification';
import Pagination from '../../common/Pagination/Pagination';
import QueueInfo from '../../common/QueueInfo';
import { useSearch } from '../../../hooks/useSearch';
import { useAuth } from '../../../hooks/useAuth';
import { Search, TaskStatus } from '../../../types/search.types';
import {
  getSearchPipeline,
  getSearchProgressPercent,
} from '../SearchDetail/searchPipelineConfig';

const SearchListComponent: React.FC = () => {
  const { searches, loading, error, pagination, fetchSearches, deleteSearch, clearError } = useSearch();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [successNotification, setSuccessNotification] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Use a ref to track if we've already fetched data
  const hasLoaded = useRef(false);
  const fetchTimeoutRef = useRef<number | null>(null);
  
  // Format error to ensure it's a string
  const formattedError = error ? (typeof error === 'string' ? error : JSON.stringify(error)) : null;
  
  // Check for fromCreate parameter in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('fromCreate') === 'true') {
      setSuccessNotification("Search created successfully! Big cities like New York may take up to 2.5 hours to collect all the data.");
    }
  }, [location.search]);
  
  // Fetch searches with pagination
  const fetchSearchesWithPagination = useCallback((page: number) => {
    fetchSearches({
      page,
      items_per_page: itemsPerPage,
    });
  }, [fetchSearches, itemsPerPage]);
  
  // Only fetch searches once when component mounts and we're authenticated
  useEffect(() => {
    // Only fetch if:
    // 1. We haven't already loaded data
    // 2. We're authenticated
    // 3. There are no searches loaded yet
    if (!hasLoaded.current && isAuthenticated && searches.length === 0) {
      console.log('SearchList: Initiating search fetch');
      
      // Clear any existing timeout
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
      
      // Set a timeout to debounce the fetch call
      fetchTimeoutRef.current = window.setTimeout(() => {
        console.log('SearchList: Fetching searches');
        fetchSearchesWithPagination(currentPage);
        hasLoaded.current = true;
        fetchTimeoutRef.current = null;
      }, 300); // 300ms debounce
    } else {
      console.log('SearchList: Skipping search fetch', {
        hasLoaded: hasLoaded.current,
        isAuthenticated,
        searchesLoaded: searches.length > 0
      });
    }
    
    // Cleanup function
    return () => {
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchSearchesWithPagination, searches.length, isAuthenticated, currentPage]); // Include isAuthenticated in dependencies
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSearchesWithPagination(page);
  };
  
  const renderStatusBadge = (search: Search) => {
    const pipeline = getSearchPipeline(search);
    const stepId = pipeline.activeStep?.id;
    const statusClass = pipeline.failed
      ? 'bg-red-100 text-red-800'
      : stepId === 'completed'
        ? 'bg-green-100 text-green-800'
      : stepId === 'decision_makers'
          ? 'bg-purple-100 text-purple-800'
          : stepId === 'contacts'
            ? 'bg-yellow-100 text-yellow-800'
            : stepId === 'preparing'
              ? 'bg-indigo-100 text-indigo-800'
              : stepId === 'pending'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-blue-100 text-blue-800';
    const displayText = pipeline.failed
      ? 'Failed'
      : pipeline.activeStep?.label || 'Pending';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
        {displayText}
      </span>
    );
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  

  
  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this search? This action cannot be undone.')) {
      await deleteSearch(id);
    }
  };
  
  // Show a simpler loading indicator if we're not authenticated yet
  if (!isAuthenticated) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <span className="text-gray-600">Please log in to view your searches</span>
        </div>
      </Card>
    );
  }
  
  if (loading && searches.length === 0) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">Loading searches...</span>
        </div>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {formattedError && (
        <Notification
          type="error"
          message={formattedError}
          onClose={clearError}
        />
      )}
      
      {successNotification && (
        <Notification
          type="success"
          message={successNotification}
          onClose={() => setSuccessNotification(null)}
        />
      )}
      
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Your Searches</h2>
        <Link to="/searches/new">
          <Button>New Search</Button>
        </Link>
      </div>
      
      {searches.length === 0 && !loading ? (
        <Card>
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">No searches found</h3>
            <p className="mt-1 text-gray-500">Create your first search to start collecting data.</p>
            <div className="mt-6">
              <Link to="/searches/new">
                <Button>Create Search</Button>
              </Link>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid gap-4">
            {[...searches]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .map((search: Search) => (
              <Card key={search.id} className="hover:shadow-md transition-shadow duration-200">
                <div className="sm:flex sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      <Link to={`/searches/${search.id}`} className="hover:text-primary">
                        {search.search_query}
                      </Link>
                    </h3>
                    <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {search.city?.name && search.city?.state_name && search.city?.country
                          ? `${search.city?.name}, ${search.city?.state_name}, ${search.city?.country}`
                          : 'Location data unavailable'}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        {formatDate(search.created_at)}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        {renderStatusBadge(search)}
                      </div>
                      {/* Queue information */}
                      {search.is_queued && (
                        <div className="mt-2">
                          <QueueInfo
                            queuePosition={search.queue_position || 0}
                            estimatedWaitMinutes={search.estimated_wait_minutes || 0}
                            variant="compact"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-shrink-0 sm:mt-0 sm:ml-5">
                    <Link to={`/searches/${search.id}`}>
                      <Button variant="secondary" size="sm" className="mr-2">
                        View
                      </Button>
                    </Link>
                    <Button 
                      variant="text" 
                      size="sm"
                      onClick={() => handleDelete(search.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                {search.status !== TaskStatus.PENDING && (
                  <div className="mt-4">
                    <div className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div>
                          <span className="text-xs font-semibold inline-block text-primary">
                            Overall progress
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-semibold inline-block text-primary">
                            {`${getSearchProgressPercent(search)}%`}
                          </span>
                        </div>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                        <div
                          style={{
                            width: `${getSearchProgressPercent(search)}%`
                          }}
                          className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                            search.status === TaskStatus.FAILED
                              ? 'bg-red-500'
                              : 'bg-primary'
                          }`}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
          
          {pagination && pagination.total_pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SearchListComponent;
